/** Low-power mode for the public viewer (item 0315).
 *
 *  A cheap totem SoC or an old phone can run the map, but not every frill on
 *  top of it. Low-power mode keeps the map, the venue's 3D blocks, search,
 *  routing and the menu exactly as they are, and drops only what costs a weak
 *  device the most for the least:
 *
 *    - pixelRatio capped at 1: a DPR-3 phone shades 9x the pixels otherwise
 *    - preserveDrawingBuffer off: forces a full-frame copy on mobile GPUs
 *    - no three.js decorative models: ~180 KB gz of script + a custom layer
 *      that draws every frame the map draws
 *    - the route line is drawn once, not animated (~60 GL frames/s otherwise,
 *      for as long as a route is on screen)
 *    - a smaller tile cache
 *
 *  It is switched on automatically when the device looks weak, and can be
 *  forced either way with ?lowpower=1 / ?lowpower=0 in the viewer URL. That is
 *  how a kiosk is provisioned (same as ?lang= and ?kiosk=), and it overrides
 *  detection in both directions, because detection can be wrong both ways.
 *
 *  Detection is deliberately conservative: a capable device must never be
 *  downgraded by accident, since that is a visible regression on the screens
 *  that sell the product. */

// Software rasterisers, and GLES2-only GPU families (no WebGL2-class work).
// Kept narrow on purpose: Mali-T6xx/T7xx and later do GLES3 and are left to
// the memory test, which is what actually catches the cheap boards they ship on.
const WEAK_RENDERER =
  /swiftshader|llvmpipe|softpipe|software rasterizer|mali-[34]\d\d|adreno \(tm\) [23]\d\d|powervr sgx|vivante|videocore/i;

function probeGL() {
  const out = { webgl2: false, renderer: "" };
  try {
    const canvas = document.createElement("canvas");
    let gl = canvas.getContext("webgl2");
    out.webgl2 = !!gl;
    if (!gl) gl = canvas.getContext("webgl") || canvas.getContext("experimental-webgl");
    if (!gl) return out;
    const info = gl.getExtension("WEBGL_debug_renderer_info");
    out.renderer = String(
      (info && gl.getParameter(info.UNMASKED_RENDERER_WEBGL)) || gl.getParameter(gl.RENDERER) || "",
    );
    // Hand the context back now: a weak device has few to spare, and the map
    // is about to ask for its own.
    gl.getExtension("WEBGL_lose_context")?.loseContext();
  } catch {
    /* no WebGL at all: the map cannot start either, nothing to decide */
  }
  return out;
}

function detect() {
  let forced = null;
  try {
    const q = new URLSearchParams(window.location.search).get("lowpower");
    if (q === "1" || q === "true") forced = true;
    if (q === "0" || q === "false") forced = false;
  } catch {
    /* ignore */
  }
  if (forced !== null) return { on: forced, reasons: [forced ? "forced on" : "forced off"] };

  const reasons = [];
  const mem = navigator.deviceMemory; // Chromium only; undefined elsewhere
  if (mem && mem <= 2) reasons.push(`deviceMemory ${mem} GB`);
  const cores = navigator.hardwareConcurrency;
  if (cores && cores <= 2) reasons.push(`${cores} CPU cores`);
  const gl = probeGL();
  if (gl.renderer && WEAK_RENDERER.test(gl.renderer)) reasons.push(`GPU ${gl.renderer}`);
  else if (gl.renderer && !gl.webgl2) reasons.push("no WebGL2");
  return { on: reasons.length > 0, reasons };
}

let cached = null;

/** { on: boolean, reasons: string[] } — decided once per page load. */
export function lowPower() {
  if (cached) return cached;
  cached = detect();
  // Visible to QA and to whoever is debugging a slow screen, without a build flag.
  try {
    document.documentElement.dataset.lowPower = cached.on ? "1" : "0";
    if (cached.on) console.info("promomaps: low-power mode (" + cached.reasons.join(", ") + ")");
  } catch {
    /* ignore */
  }
  return cached;
}
